import React, {
  FC,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import {
  ContactWhiteIcon,
  HelpWhiteIcon,
  WalletWhiteIcon,
  SettingsWhiteIcon,
} from 'assets/icons';

import { RoutesList } from 'enums/routes-list.enum';
import {
  accessTokenSelector,
  userPendingSelector,
  userSelector,
} from 'app/auth/store/auth.selectors';
import { updateProfilePicture } from './store/profile.actions';
import { picturePendingSelector } from './store/profile.selectors';
import { getUser } from 'app/auth/store/auth.actions';
import { handleMailTo } from 'handlers/handle-mailto';

import ArrowForwardIos from '@mui/icons-material/ArrowForwardIos';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import AppHeader from 'components/app-header';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import AppBackdrop from 'components/app-backdrop';
import CropPictureModal from './crop-picture-modal';
import { colors } from 'constants/colors';

const SettingsPages = React.lazy(
  () => import(/* webpackChunkName: "pages_profile-settings" */ 'app/settings')
);
const WalletPages = React.lazy(
  () => import(/* webpackChunkName: "pages_profile-wallet" */ 'app/wallet')
);

const ProfilePages: FC = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const user = useSelector(userSelector);
  const access_token = useSelector(accessTokenSelector);
  const picture_pending = useSelector(picturePendingSelector);
  const user_pending = useSelector(userPendingSelector);
  const isPicturePending = useMemo(() => {
    return user_pending || picture_pending;
  }, [picture_pending, user_pending]);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isCropOpen, setIsCropOpen] = useState(false);

  const navigateSettings = () => navigate(RoutesList.Settings_Default);
  const navigateWallet = () => navigate(RoutesList.Wallet_Default);
  const navigateHelp = () => window.open(process.env.REACT_APP_FAQ_URL);

  const onSelectImage = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        setSelectedFile(e.target.files[0]);
      }
    },
    []
  );

  const handleCloseCropModal = useCallback(() => {
    setSelectedFile(null);
  }, []);

  const handleResendAvatar = useCallback(() => {
    if (!access_token) {
      return;
    }
    dispatch<any>(getUser({ user_id: access_token.sub.userId }));
  }, [access_token, dispatch]);

  const updatePicture = useCallback(
    async (croppedFile: File) => {
      if (!access_token) {
        return;
      }
      await dispatch<any>(
        updateProfilePicture({
          user_id: access_token.sub.userId,
          image: croppedFile,
        })
      );
      dispatch<any>(getUser({ user_id: access_token.sub.userId }));
    },
    [dispatch, access_token]
  );

  useEffect(() => {
    if (selectedFile) {
      setIsCropOpen(true);
    }
  }, [selectedFile]);

  useEffect(() => {
    if (picture_pending) {
      setIsCropOpen(false);
      setSelectedFile(null);
    }
  }, [picture_pending]);

  if (user && !user.username) {
    return <Navigate to={RoutesList.Onboarding_Default} />;
  }

  return (
    <>
      <AppHeader user={user} />
      <Grid
        sx={{
          borderRight: '1px solid rgba(159, 159, 159, .2)',
          borderLeft: '1px solid rgba(159, 159, 159, .2)',
          flex: '1 1 256px',
        }}
      >
        <Box sx={{ padding: '36px 0', flex: '1 1' }}>
          <List>
            <ListItem>
              <ListItemText
                secondary={`hi ${user?.username} 👋`}
                secondaryTypographyProps={{
                  component: 'h2',
                  color: colors.default.info,
                  textTransform: 'uppercase',
                  sx: { fontSize: '18px', fontFamily: 'Avenir' },
                }}
              />
            </ListItem>
            <ListItem>
              <Badge
                color="primary"
                overlap="circular"
                badgeContent={
                  <IconButton
                    component="label"
                    sx={{
                      background: 'rgba(39, 39, 39, 1) !important',
                      border: '2px solid rgba(172, 251, 104, 1) !important',
                    }}
                  >
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      value={''}
                      onChange={onSelectImage}
                    />
                    <PhotoCameraIcon
                      color="primary"
                      sx={{ lineHeight: 0, fontSize: '14px' }}
                    />
                  </IconButton>
                }
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                componentsProps={{
                  badge: {
                    style: {
                      background: 'transparent',
                      padding: '12px',
                    },
                  },
                }}
              >
                <ListItemAvatar>
                  {isPicturePending && (
                    <AppBackdrop
                      styles={{
                        width: '121px',
                        height: '121px',
                        borderRadius: '50%',
                        background: '#3A3A3A',
                      }}
                    />
                  )}
                  {!isPicturePending && (
                    <Avatar
                      sx={{
                        width: '121px',
                        height: '121px',
                        background: '#3A3A3A',
                      }}
                      src={user?.profilePictureUrl}
                      onError={handleResendAvatar}
                    >
                      {!user?.profilePictureUrl && (
                        <Typography
                          variant="body1"
                          color="white"
                          sx={{
                            fontFamily: 'Modak',
                            fontSize: '66px',
                            textTransform: 'uppercase',
                            lineHeight: 0,
                            margin: '11px 0 0 6px',
                          }}
                        >
                          {user?.username?.substring(0, 1)}
                        </Typography>
                      )}
                    </Avatar>
                  )}
                </ListItemAvatar>
              </Badge>
            </ListItem>

            <br />

            <ListItem
              color="white"
              disablePadding
              secondaryAction={
                <ListItemIcon
                  color="inherit"
                  sx={{ justifyContent: 'flex-end' }}
                >
                  <ArrowForwardIos sx={{ fontSize: 'inherit' }} />
                </ListItemIcon>
              }
            >
              <ListItemButton
                variant="navigation"
                color="white"
                size="medium"
                selected={pathname.indexOf('/wallet') > -1}
                onClick={navigateWallet}
              >
                <ListItemIcon color="inherit">
                  <WalletWhiteIcon />
                </ListItemIcon>
                <ListItemText
                  secondary={t('navigations.wallet')}
                  secondaryTypographyProps={{
                    sx: { textTransform: 'none !important', fontSize: '18px' },
                  }}
                />
              </ListItemButton>
            </ListItem>

            <ListItem
              color="white"
              disablePadding
              secondaryAction={
                <ListItemIcon
                  color="inherit"
                  sx={{ justifyContent: 'flex-end' }}
                >
                  <ArrowForwardIos sx={{ fontSize: 'inherit' }} />
                </ListItemIcon>
              }
            >
              <ListItemButton
                variant="navigation"
                color="white"
                size="medium"
                onClick={navigateHelp}
              >
                <ListItemIcon color="inherit">
                  <HelpWhiteIcon />
                </ListItemIcon>
                <ListItemText
                  secondary={t('navigations.help')}
                  secondaryTypographyProps={{
                    sx: { textTransform: 'none !important', fontSize: '18px' },
                  }}
                />
              </ListItemButton>
            </ListItem>

            <ListItem
              color="white"
              disablePadding
              secondaryAction={
                <ListItemIcon
                  color="inherit"
                  sx={{ justifyContent: 'flex-end' }}
                >
                  <ArrowForwardIos sx={{ fontSize: 'inherit' }} />
                </ListItemIcon>
              }
            >
              <ListItemButton
                variant="navigation"
                color="white"
                size="medium"
                selected={pathname.indexOf('/settings') > -1}
                onClick={navigateSettings}
              >
                <ListItemIcon color="inherit">
                  <SettingsWhiteIcon />
                </ListItemIcon>
                <ListItemText
                  secondary={t('navigations.settings')}
                  secondaryTypographyProps={{
                    sx: { textTransform: 'none !important', fontSize: '18px' },
                  }}
                />
              </ListItemButton>
            </ListItem>

            <ListItem
              color="white"
              disablePadding
              secondaryAction={
                <ListItemIcon
                  color="inherit"
                  sx={{ justifyContent: 'flex-end' }}
                >
                  <ArrowForwardIos sx={{ fontSize: 'inherit' }} />
                </ListItemIcon>
              }
            >
              <ListItemButton
                variant="navigation"
                color="white"
                size="medium"
                onClick={handleMailTo}
              >
                <ListItemIcon color="inherit">
                  <ContactWhiteIcon />
                </ListItemIcon>
                <ListItemText
                  secondary={t('navigations.contact')}
                  secondaryTypographyProps={{
                    sx: { textTransform: 'none !important', fontSize: '18px' },
                  }}
                />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      </Grid>

      {isCropOpen && selectedFile && (
        <CropPictureModal
          file={selectedFile}
          onClose={handleCloseCropModal}
          udpatePicture={updatePicture}
        />
      )}

      <Grid
        sx={{
          flex: '1 1 601px',
          flexDirection: 'column',
          display: 'flex',
          borderRight: '1px solid rgba(159, 159, 159, .2)',
        }}
      >
        <Routes>
          <Route path={RoutesList.Default} element={<></>} />
          <Route
            path={RoutesList.Settings_Short_All}
            element={
              <Suspense>
                <SettingsPages />
              </Suspense>
            }
          />
          <Route
            path={RoutesList.Wallet_Short_All}
            element={
              <Suspense>
                <WalletPages />
              </Suspense>
            }
          />

          <Route
            path={RoutesList.All}
            element={<Navigate to={RoutesList.Profile_Default} />}
          />
        </Routes>
      </Grid>
    </>
  );
};

export default ProfilePages;
